import { useCallback, useEffect, useState } from 'react';

import { ItemsQuery } from '@/types/ItemsQuery';

import { BrowseActiveFilter } from '../types';
import { readKeywordFromUrl, writeKeywordToUrl } from '../utils/urlSearchParam';

export interface UseBrowseQueryResult {
    query: ItemsQuery;
    setQuery: (
        update: Partial<ItemsQuery> | ((previous: ItemsQuery) => ItemsQuery),
    ) => void;
    activeFilters: Record<string, BrowseActiveFilter>;
    setActiveFilter: (type: string, filter: BrowseActiveFilter) => void;
    removeFilter: (filter: BrowseActiveFilter) => void;
}

function createInitialQuery(): ItemsQuery {
    return {
        keyword: readKeywordFromUrl(),
    };
}

/**
 * Single source of truth for the browse items query passed to {@link ItemsList}.
 * Downstream filter tickets (LAX-10134, LAX-10136) attach via
 * `setActiveFilter` / `removeFilter` without forking query state.
 */
export function useBrowseQuery(): UseBrowseQueryResult {
    const [query, setQueryState] = useState<ItemsQuery>(createInitialQuery);
    const [activeFilters, setActiveFilters] = useState<Record<string, BrowseActiveFilter>>({});

    const setQuery = useCallback(
        (update: Partial<ItemsQuery> | ((previous: ItemsQuery) => ItemsQuery)) => {
            setQueryState((previous) => {
                const next =
                    typeof update === 'function'
                        ? update(previous)
                        : { ...previous, ...update };

                if ((next.keyword ?? '') !== (previous.keyword ?? '')) {
                    writeKeywordToUrl(next.keyword ?? '');
                }

                return next;
            });
        },
        [],
    );

    const setActiveFilter = useCallback(
        (type: string, filter: BrowseActiveFilter) => {
            setActiveFilters((previous) => ({ ...previous, [type]: filter }));
            if (filter.itemsQuery) {
                setQuery((previous) => ({ ...previous, ...filter.itemsQuery }));
            }
        },
        [setQuery],
    );

    const removeFilter = useCallback(
        (filter: BrowseActiveFilter) => {
            setActiveFilters((previous) => {
                const next = { ...previous };
                delete next[filter.type];
                return next;
            });

            if (filter.itemsQuery) {
                const { itemsQuery } = filter;
                setQuery((previous) => {
                    const next = { ...previous };
                    for (const key of Object.keys(itemsQuery)) {
                        delete next[key as keyof ItemsQuery];
                    }
                    return next;
                });
            }
        },
        [setQuery],
    );

    useEffect(() => {
        const syncKeywordFromUrl = () => {
            const urlKeyword = readKeywordFromUrl();
            setQueryState((previous) =>
                (previous.keyword ?? '') === urlKeyword
                    ? previous
                    : { ...previous, keyword: urlKeyword },
            );
        };

        window.addEventListener('popstate', syncKeywordFromUrl);

        try {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const angularGlobal = (window as any).angular;
            const injector = angularGlobal?.element(document.body)?.injector?.();
            const $rootScope = injector?.get('$rootScope');
            if ($rootScope?.$on) {
                const off = $rootScope.$on('$routeUpdate', syncKeywordFromUrl);
                return () => {
                    window.removeEventListener('popstate', syncKeywordFromUrl);
                    off();
                };
            }
        } catch {
            // Storybook / unit tests: popstate only.
        }

        return () => {
            window.removeEventListener('popstate', syncKeywordFromUrl);
        };
    }, []);

    return {
        query,
        setQuery,
        activeFilters,
        setActiveFilter,
        removeFilter,
    };
}
